import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { AiQuizStatus, ClassroomDetail, ClassSessionDetail, Quiz } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { fromLocalInput, toLocalInput } from '../calendar/dates';
import { formatWhen } from '../events/shared';
import { useMe } from '../session/SessionProvider';
import { QuizCard, QuizEditor } from './QuizzesTab';
import { PARTICIPATION, Verses } from './shared';
import { CitationChooser, VersePicker } from './VersePicker';
import { ClassLive } from './ClassLive';
import { PAGE_BREAK } from './ClassReader';

/** Una clase: tema, versículos, notas, tarea, materiales, cuestionarios y asistencia con participación. */
export function SessionPage() {
  const { classroomId = '', sessionId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const roomBase = `/organization/churches/${churchId}/classrooms/${classroomId}`;
  const base = `${roomBase}/sessions/${sessionId}`;
  const data = useAsync(
    async (signal) => {
      if (!churchId) return null;
      const session = await api.get<ClassSessionDetail>(base, signal);
      const room = session.canTeach ? await api.get<ClassroomDetail>(roomBase, signal) : null;
      return { session, room };
    },
    [base],
  );
  const [feedback, setFeedback] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const location = useLocation();
  // Desde el panel del maestro: «Notas» abre la edición; «Tomar asistencia» y «Cuestionario» llevan a su sección.
  const [editing, setEditing] = useState(params.get('editar') === '1');
  const ready = data.status === 'ready';
  useEffect(() => {
    if (ready && location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView?.({ block: 'start' });
  }, [ready, location.hash]);
  const [editingQuiz, setEditingQuiz] = useState<Quiz | null>(null);
  const [newQuiz, setNewQuiz] = useState(false);

  async function run(action: () => Promise<unknown>, ok?: string) {
    setBusy(true);
    setFeedback(null);
    try {
      await action();
      if (ok) setFeedback({ tone: 'success', text: ok });
      data.reload();
      return true;
    } catch (e) {
      setFeedback({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  if (!data.data) return null;
  const { session: s, room } = data.data;

  return (
    <div className={`page${room?.ministry.kids ? ' kids' : ''}`}>
      <p className="small">
        <Link to={`/app/salones/${s.classroom.id}`}>← {s.classroom.name}</Link>
      </p>
      <header className="page__header page__header--actions">
        <div>
          <h1>{s.topic ? s.topic.title : 'Clase'}</h1>
          <p className="muted">
            {formatWhen(s.startsAt, s.endsAt)} {s.canceled ? <span className="badge">Cancelada</span> : null}
          </p>
        </div>
        {s.canTeach && !editing ? (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Editar clase
          </Button>
        ) : null}
      </header>
      {feedback ? <Alert tone={feedback.tone}>{feedback.text}</Alert> : null}

      {editing && room ? (
        <EditSession s={s} room={room} roomBase={roomBase} busy={busy} onCancel={() => setEditing(false)} onSave={(body) => void run(() => api.put(base, body), 'Clase actualizada.').then((ok) => ok && setEditing(false))} />
      ) : (
        <Card title="La clase">
          <dl className="details">
            <dt>Tema</dt>
            <dd>{s.topic ? s.topic.title : <span className="muted">Sin tema</span>}</dd>
            <dt>Versículos</dt>
            <dd>{s.verses ? <Verses text={s.verses} invalid={s.invalidVerses} /> : <span className="muted">—</span>}</dd>
          </dl>
        </Card>
      )}
      {/* Lo que más usa el maestro va justo debajo de «La clase». */}
      {s.canTeach && !editing ? (
        <div id="asistencia">
          <Attendance s={s} busy={busy} onSave={(items) => void run(() => api.put(`${base}/attendance`, { items }), 'Asistencia guardada.')} />
          {s.attendance.some((a) => a.present === false) ? <AbsentNotice base={base} count={s.attendance.filter((a) => a.present === false).length} /> : null}
        </div>
      ) : null}
      {!editing && churchId ? (
        <ClassLive churchId={churchId} s={s} roomBase={roomBase} students={s.canTeach ? s.attendance.map((a) => ({ personId: a.person.personId, name: a.person.name })) : []} />
      ) : null}

      {s.canTeach && !editing && !editingQuiz && !newQuiz && s.quizzes.length === 0 ? (
        <div id="cuestionario">
          <Card title="Cuestionario">
            <AiQuizOption
              base={base}
              onCreated={async (id) => {
                const fresh = await api.get<ClassSessionDetail>(base);
                setEditingQuiz(fresh.quizzes.find((q) => q.id === id) ?? null);
                setFeedback({ tone: 'success', text: 'Listo. Revisa las preguntas, corrige lo que haga falta y publícalo: los alumnos no lo ven hasta que lo publiques.' });
                data.reload();
              }}
            />
            <p className="small muted">Escribe las preguntas de esta clase. Los alumnos no lo ven hasta que lo publiques.</p>
            <Button disabled={!room} onClick={() => setNewQuiz(true)}>
              Escribir preguntas
            </Button>
          </Card>
        </div>
      ) : null}

      {newQuiz && room ? <QuizEditor quiz={null} sessionId={s.id} sessions={room.sessions} quizzes={room.quizzes} base={roomBase} run={run} busy={busy} onDone={() => setNewQuiz(false)} /> : null}
      {editingQuiz && room ? <QuizEditor quiz={editingQuiz} sessions={room.sessions} quizzes={room.quizzes} base={roomBase} run={run} busy={busy} onDone={() => setEditingQuiz(null)} /> : null}

      {s.canTeach && !editing ? <LiveQuestions base={base} /> : null}

      {s.documents.length > 0 ? (
        <Card title="Materiales de esta clase">
          <ul className="list">
            {s.documents.map((d) => (
              <li key={d.id} className="list__item">
                <strong>{d.title}</strong>
                <span className="actions">
                  <a className="btn btn--ghost" href={`/api${roomBase}/documents/${d.id}/file?ver=1`} target="_blank" rel="noopener noreferrer">
                    Ver
                  </a>
                  <a className="btn btn--ghost" href={`/api${roomBase}/documents/${d.id}/file`} download>
                    Descargar
                  </a>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {s.quizzes.length > 0 && !editingQuiz ? (
        <>
          <h2 id="cuestionario">Cuestionarios de esta clase</h2>
          {s.quizzes.map((q) => (
            <QuizCard key={q.id} quiz={q} canTeach={s.canTeach} base={roomBase} run={run} busy={busy} onEdit={s.canTeach && room ? () => setEditingQuiz(q) : undefined} />
          ))}
          {s.canTeach ? (
            <p className="small">
              <Link to={`/app/salones/${s.classroom.id}?ver=temario`}>Editar cuestionarios en el salón</Link>
            </p>
          ) : null}
        </>
      ) : null}

      {!s.canTeach && s.attendance[0] && s.attendance[0].present !== null ? (
        <Card title="Mi asistencia">
          <p>
            {s.attendance[0].present ? 'Asististe' : 'No asististe'}
            {s.attendance[0].participation !== null ? ` · Participación: ${PARTICIPATION[s.attendance[0].participation]}` : ''}
          </p>
        </Card>
      ) : null}
    </div>
  );
}

const COUNTS = Array.from({ length: 21 }, (_, i) => i);

/**
 * «Generar con IA», arriba del cuestionario hecho a mano (módulo con costo de Facturación): la IA lee las
 * notas y los versículos de la clase y deja un borrador para revisar. Sin el módulo, solo una línea con
 * candado que explica que la administración lo contrata en Facturación.
 */
function AiQuizOption({ base, onCreated }: { base: string; onCreated: (quizId: string) => Promise<void> }) {
  const status = useAsync((signal) => api.get<AiQuizStatus>(`${base}/ai-quiz`, signal), [base]);
  const [shown, setShown] = useState(false);
  const [choice, setChoice] = useState(5);
  const [trueFalse, setTrueFalse] = useState(3);
  const [open, setOpen] = useState(2);
  const total = choice + trueFalse + open;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (status.status !== 'ready') return null;
  const st = status.data;
  if (st.hasQuiz || (!st.configured && !st.contracted)) return null;
  if (!st.contracted) {
    return (
      <p className="small muted">
        <span aria-hidden="true">🔒 </span>Generar con IA: es un módulo extra con costo mensual que la administración de la iglesia contrata en <Link to="/app/facturacion">Facturación</Link>.
      </p>
    );
  }
  const left = Math.max(0, st.monthlyLimit - st.used);
  const blocked = !st.configured
    ? 'Falta configurar la IA. Avísale a la administración de KEORA.'
    : !st.inGoodStanding
    ? 'La suscripción de tu iglesia no está al corriente. Revisa Facturación.'
    : left === 0
      ? `Tu iglesia ya usó los ${st.monthlyLimit} cuestionarios con IA de este mes. El tope se renueva el día 1.`
      : !st.hasContent
        ? 'Escribe primero las notas o los versículos de la clase («Editar clase»): de ahí salen las preguntas.'
        : null;

  async function generate() {
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.post<{ id: string }>(`${base}/ai-quiz`, { choice, trueFalse, open });
      await onCreated(id);
      status.reload();
    } catch (e) {
      setError(errorMessage(e));
      status.reload();
    } finally {
      setBusy(false);
    }
  }

  if (!shown) {
    return (
      <div className="actions">
        <Button variant="secondary" onClick={() => setShown(true)}>
          Generar con IA
        </Button>
      </div>
    );
  }
  const count = (id: string, label: string, value: number, set: (n: number) => void) => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value} onChange={(e) => set(Number(e.target.value))}>
        {COUNTS.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </div>
  );
  return (
    <section className="room-add" aria-label="Generar con IA">
      {blocked ? (
        <p className="muted">{blocked}</p>
      ) : (
        <>
          <p className="small muted">
            La IA lee el tema, las notas y los versículos de esta clase y prepara un borrador que tú revisas antes de publicarlo. Te quedan {left} de {st.monthlyLimit} este mes.
          </p>
          <div className="form__row">
            {count('ai-quiz-choice', 'Opción múltiple', choice, setChoice)}
            {count('ai-quiz-tf', 'Cierto o falso', trueFalse, setTrueFalse)}
            {count('ai-quiz-open', 'Abiertas', open, setOpen)}
          </div>
        </>
      )}
      {!blocked && (total < 3 || total > 20) ? <p className="small" role="alert">En total deben ser entre 3 y 20 preguntas (ahora son {total}).</p> : null}
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        <Button busy={busy} disabled={blocked !== null || total < 3 || total > 20} onClick={() => void generate()}>
          {busy ? 'Preparando preguntas…' : 'Generar cuestionario automáticamente'}
        </Button>
        <Button variant="ghost" onClick={() => setShown(false)}>
          Cancelar
        </Button>
      </div>
    </section>
  );
}

/** «Tema del temario» con «+ Nuevo tema»: lo crea al final del temario y lo deja elegido. */
function TopicField({ roomBase, initial, topics: start }: { roomBase: string; initial: string; topics: ClassroomDetail['topics'] }) {
  const [topics, setTopics] = useState(start);
  const [value, setValue] = useState(initial);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`${roomBase}/topics`, { items: [{ title: title.trim(), description: description.trim() || null }] });
      const fresh = (await api.get<ClassroomDetail>(roomBase)).topics;
      setTopics(fresh);
      const made = [...fresh].reverse().find((t) => t.title === title.trim());
      if (made) setValue(made.id);
      setAdding(false);
      setTitle('');
      setDescription('');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="field">
      <label htmlFor="ses-topic">Tema del temario</label>
      <div className="topic-field">
        <select id="ses-topic" name="topicId" value={value} onChange={(e) => setValue(e.target.value)}>
          <option value="">Sin tema</option>
          {topics.map((t) => (
            <option key={t.id} value={t.id}>
              {t.position + 1}. {t.title}
            </option>
          ))}
        </select>
        {!adding ? (
          <Button type="button" variant="ghost" onClick={() => setAdding(true)}>
            + Nuevo tema
          </Button>
        ) : null}
      </div>
      {adding ? (
        <div className="topic-field__new">
          <Field label="Título del tema" value={title} maxLength={150} onChange={(e) => setTitle(e.target.value)} />
          <Field label="Descripción (opcional)" value={description} maxLength={2000} onChange={(e) => setDescription(e.target.value)} />
          {error ? <p className="small text-danger" role="alert">{error}</p> : null}
          <div className="actions">
            <Button type="button" variant="secondary" busy={busy} disabled={!title.trim()} onClick={() => void create()}>
              Crear tema
            </Button>
            <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Preguntas preparadas antes de la clase para lanzarlas en vivo con un toque. */
function LiveQuestions({ base }: { base: string }) {
  const list = useAsync((signal) => api.get<{ items: { id: string; question: string; options: string[] }[] }>(`${base}/questions`, signal).then((r) => r.items), [base]);
  const [question, setQuestion] = useState('');
  const [yesNo, setYesNo] = useState(true);
  const [options, setOptions] = useState(['', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const items = list.status === 'ready' ? list.data : [];
  return (
    <details className="card live-questions" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>Preguntas para el vivo ({items.length})</summary>
      <p className="small muted">Prepáralas antes de la clase y lánzalas con un toque desde «❓ Pregunta» en la barra del vivo.</p>
      <ul className="list">
        {items.map((q) => (
          <li key={q.id} className="list__item list__item--actions">
            <span>
              {q.question} <span className="small muted">({q.options.join(' / ')})</span>
            </span>
            <Button variant="ghost" onClick={() => void api.delete(`${base}/questions/${q.id}`).then(list.reload)} aria-label={`Quitar «${q.question}»`}>
              Quitar
            </Button>
          </li>
        ))}
      </ul>
      <form
        className="teacher-tools__poll"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          try {
            await api.post(`${base}/questions`, { question: question.trim(), options: yesNo ? ['Sí', 'No'] : options.filter((o) => o.trim()) });
            setQuestion('');
            setOptions(['', '', '']);
            list.reload();
          } catch (err) {
            setError(errorMessage(err));
          }
        }}
      >
        <input aria-label="Nueva pregunta para el vivo" placeholder="¿…?" value={question} maxLength={300} onChange={(e) => setQuestion(e.target.value)} required />
        <label className="check small">
          <input type="checkbox" checked={yesNo} onChange={(e) => setYesNo(e.target.checked)} /> Sí / No
        </label>
        {!yesNo
          ? options.map((o, i) => (
              <input key={i} aria-label={`Opción ${i + 1} de la pregunta`} placeholder={`Opción ${i + 1}`} value={o} maxLength={120} onChange={(e) => setOptions((xs) => xs.map((x, j) => (j === i ? e.target.value : x)))} />
            ))
          : null}
        {error ? <p className="small text-danger">{error}</p> : null}
        <Button type="submit" variant="secondary">
          Guardar pregunta
        </Button>
      </form>
    </details>
  );
}

/** Mensaje amable a quien faltó, con enlace a la clase. */
function AbsentNotice({ base, count }: { base: string; count: number }) {
  const [message, setMessage] = useState('Te extrañamos en la clase. Aquí está lo que vimos; ¡te esperamos la próxima!');
  const [state, setState] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Card title={`Avisar a quien faltó (${count})`}>
      <textarea aria-label="Mensaje para quien faltó" rows={2} maxLength={500} value={message} onChange={(e) => setMessage(e.target.value)} />
      {state ? <Alert tone={state.tone}>{state.text}</Alert> : null}
      <Button
        busy={busy}
        onClick={async () => {
          setBusy(true);
          setState(null);
          try {
            const r = await api.post<{ sent: number }>(`${base}/absent-notice`, { message: message.trim() || null });
            setState({ tone: 'success', text: r.sent === 1 ? 'Aviso enviado a 1 persona.' : `Aviso enviado a ${r.sent} personas.` });
          } catch (e) {
            setState({ tone: 'error', text: errorMessage(e) });
          } finally {
            setBusy(false);
          }
        }}
      >
        Enviar aviso con enlace a la clase
      </Button>
    </Card>
  );
}

type LibraryItem = { id: string; kind: string; title: string; author: string | null; file: { mime: string } | null };

/** Citar un fragmento corto de un libro de la Biblioteca: libro → página → fragmento (del texto de la página o pegado). */
function LibraryCite({ roomBase, onInsert }: { roomBase: string; onInsert: (label: string) => void }) {
  const churchId = roomBase.split('/')[3]!;
  const items = useAsync((signal) => api.get<{ items: LibraryItem[] }>(`/media/churches/${churchId}/library`, signal).then((r) => r.items.filter((i) => i.file)), [churchId]);
  const [itemId, setItemId] = useState('');
  const [page, setPage] = useState(1);
  const [pageText, setPageText] = useState<string | null>(null);
  const [fragment, setFragment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pageRef = useRef<HTMLTextAreaElement>(null);

  async function loadPage() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.get<{ pages: number | null; text: string | null }>(`/media/churches/${churchId}/library/${itemId}/page-text?page=${page}`);
      setPageText(r.text);
      if (!r.text) setError(r.pages && page > r.pages ? `El libro tiene ${r.pages} páginas.` : 'Esta página no trae texto (p. ej. un libro escaneado): escribe o pega el fragmento abajo.');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const c = await api.post<{ label: string }>(`${roomBase}/citations`, { libraryItemId: itemId, page, fragment: fragment.trim() });
      onInsert(c.label);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="library-cite">
      <div className="form__row">
        <div className="field">
          <label htmlFor="lc-item">Libro de la Biblioteca</label>
          <select id="lc-item" value={itemId} onChange={(e) => { setItemId(e.target.value); setPageText(null); }}>
            <option value="">{items.status === 'ready' ? (items.data.length ? 'Escoge…' : 'La Biblioteca no tiene libros con archivo') : 'Cargando…'}</option>
            {items.status === 'ready'
              ? items.data.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.title}
                    {i.author ? ` · ${i.author}` : ''}
                  </option>
                ))
              : null}
          </select>
        </div>
        <Field label="Página" type="number" min={1} max={5000} value={page} onChange={(e) => { setPage(Math.max(1, Number(e.target.value) || 1)); setPageText(null); }} />
      </div>
      <Button type="button" variant="secondary" busy={busy} disabled={!itemId} onClick={() => void loadPage()}>
        Ver el texto de la página
      </Button>
      {pageText ? (
        <div className="field">
          <label htmlFor="lc-page">Texto de la página {page} (selecciona el fragmento)</label>
          <textarea
            id="lc-page"
            ref={pageRef}
            rows={6}
            readOnly
            value={pageText}
            onSelect={() => {
              const el = pageRef.current;
              if (el && el.selectionEnd > el.selectionStart) setFragment(el.value.slice(el.selectionStart, el.selectionEnd).slice(0, 1500));
            }}
          />
        </div>
      ) : null}
      <div className="field">
        <label htmlFor="lc-fragment">Fragmento que citas (máximo 1 500 caracteres)</label>
        <textarea id="lc-fragment" rows={3} maxLength={1500} value={fragment} onChange={(e) => setFragment(e.target.value)} />
      </div>
      {error ? <p className="small text-danger" role="alert">{error}</p> : null}
      <Button type="button" busy={busy} disabled={!itemId || !fragment.trim()} onClick={() => void save()}>
        Insertar la cita
      </Button>
    </div>
  );
}

/** Notas de la clase con «Insertar versículo»: la cita se escribe donde está el cursor y en la clase se abre al tocarla. */
function NotesField({ initial, roomBase }: { initial: string | null; roomBase: string }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [picking, setPicking] = useState(false);
  const [citing, setCiting] = useState(false);
  const insert = (citation: string) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    const before = el.value.slice(0, start);
    const text = `${before && !/\s$/.test(before) ? ' ' : ''}${citation} `;
    el.setRangeText(text, start, end, 'end');
    el.focus();
    setPicking(false);
  };
  /** Divide la lectura: lo que sigue se ve en la página siguiente. */
  const pageBreak = () => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const before = el.value.slice(0, start);
    el.setRangeText(`${before && !before.endsWith('\n') ? '\n' : ''}${PAGE_BREAK}\n`, start, el.selectionEnd ?? start, 'end');
    el.focus();
  };
  return (
    <div className="field">
      <label htmlFor="ses-notes">Notas de la clase</label>
      <div className="actions">
        <Button type="button" variant="ghost" onClick={() => setPicking((v) => !v)}>
          {picking ? 'Cerrar' : '+ Insertar versículo'}
        </Button>
        <Button type="button" variant="ghost" onClick={pageBreak}>
          + Insertar salto de página
        </Button>
        <Button type="button" variant="ghost" onClick={() => setCiting((v) => !v)}>
          {citing ? 'Cerrar' : '+ Citar de la Biblioteca'}
        </Button>
      </div>
      {citing ? (
        <LibraryCite
          roomBase={roomBase}
          onInsert={(label) => {
            insert(`⟪${label}⟫`);
            setCiting(false);
          }}
        />
      ) : null}
      {picking ? <CitationChooser onAdd={insert} action="Insertar" idPrefix="nv" /> : null}
      <textarea id="ses-notes" ref={ref} name="notes" rows={12} maxLength={50000} defaultValue={initial ?? ''} />
      <p className="small muted">Las citas como «Lamentaciones 3:21-26» se abren al tocarlas en la clase. Con «salto de página», los alumnos leen una página a la vez.</p>
    </div>
  );
}

function EditSession({ s, room, roomBase, busy, onSave, onCancel }: { s: ClassSessionDetail; room: ClassroomDetail; roomBase: string; busy: boolean; onSave: (body: object) => void; onCancel: () => void }) {
  return (
    <Card title="Editar clase">
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          const t = (k: string) => String(f.get(k) ?? '').trim() || null;
          onSave({
            startsAt: fromLocalInput(String(f.get('startsAt'))),
            endsAt: fromLocalInput(String(f.get('endsAt'))),
            topicId: t('topicId'),
            verses: t('verses'),
            notes: t('notes'),
            // La tarea ya no se pide; lo que estaba guardado se conserva.
            homework: s.homework,
            canceled: f.get('canceled') === 'on',
          });
        }}
      >
        <TopicField roomBase={roomBase} initial={s.topic?.id ?? ''} topics={room.topics} />
        <div className="form__row">
          <Field label="Empieza" name="startsAt" type="datetime-local" required defaultValue={toLocalInput(new Date(s.startsAt))} />
          <Field label="Termina" name="endsAt" type="datetime-local" required defaultValue={toLocalInput(new Date(s.endsAt))} />
        </div>
        <VersePicker initial={s.verses} invalid={s.invalidVerses} />
        <NotesField initial={s.notes} roomBase={roomBase} />
        <label className="check">
          <input type="checkbox" name="canceled" defaultChecked={s.canceled} /> Clase cancelada
        </label>
        <div className="actions">
          <Button type="submit" busy={busy}>
            Guardar
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * Asistencia rápida: tocar el nombre marca presente (y otra vez, ausente). Al marcarlo aparece su
 * participación en 4 niveles. «Guardar» queda fijo abajo para no tener que bajar hasta el final.
 */
function Attendance({ s, busy, onSave }: { s: ClassSessionDetail; busy: boolean; onSave: (items: { personId: string; present: boolean; participation: number | null }[]) => void }) {
  // Lista nueva: todos vienen como presentes y se toca a quien faltó.
  const fresh = s.attendance.every((a) => a.present === null);
  const [rows, setRows] = useState(s.attendance.map((a) => ({ personId: a.person.personId, name: a.person.name, present: a.present ?? fresh, participation: a.participation, online: a.online ?? false })));
  const presentCount = rows.filter((r) => r.present).length;
  const set = (i: number, patch: Partial<(typeof rows)[number]>) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  if (rows.length === 0) return <Card title="Asistencia"><p className="muted">El salón no tiene alumnos.</p></Card>;
  return (
    <Card title={`Asistencia (${presentCount} de ${rows.length})`}>
      <p className="small muted">
        {fresh ? 'Todos vienen marcados como presentes: toca el nombre de quien faltó.' : 'Toca el nombre para cambiar entre presente y falta.'} Presentes: {presentCount} · Faltas: {rows.length - presentCount}
      </p>
      <div className="actions">
        <Button variant="ghost" onClick={() => setRows((rs) => rs.map((r) => ({ ...r, present: true })))}>
          Todos presentes
        </Button>
      </div>
      <ul className="attend">
        {rows.map((r, i) => (
          <li key={r.personId} className={`attend__row${r.present ? ' attend__row--present' : ''}`}>
            <button type="button" className="attend__name" aria-pressed={r.present} onClick={() => set(i, { present: !r.present, ...(r.present ? { participation: null } : {}) })}>
              <span className="attend__mark" aria-hidden="true">{r.present ? '✓' : ''}</span>
              {r.name}
              {r.online && r.present ? <span className="badge attend__online">en línea</span> : null}
            </button>
            {r.present ? (
              <div className="attend__levels" role="group" aria-label={`Participación de ${r.name}`}>
                {PARTICIPATION.map((p, k) => (
                  <button key={p} type="button" className={`attend__level${r.participation === k ? ' attend__level--on' : ''}`} aria-pressed={r.participation === k} onClick={() => set(i, { participation: r.participation === k ? null : k })}>
                    {p}
                  </button>
                ))}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      <div className="sticky-save">
        <Button busy={busy} className="btn--block" onClick={() => onSave(rows.map(({ personId, present, participation }) => ({ personId, present, participation })))}>
          Guardar asistencia ({presentCount} de {rows.length})
        </Button>
      </div>
    </Card>
  );
}
